<script lang="ts">
    import { onMount, onDestroy, getContext, tick } from "svelte";
    import { api, loader, config, isDisplayedBrandsPopup } from "../stores";
    import {
        MAX_ZOOM_VALUE,
        MIN_ZOOM_VALUE,
        Page,
        PagesViewSlideshow,
        PageThumb,
        TransitionMode,
        ZOOM_STEP,
    } from "../model";
    import Catalog_PageView from "./Catalog_PageView.svelte";
    import { fly } from "svelte/transition";
    import { Link, navigate } from "svelte-navigator";
    import Slideshow from "./Slideshow.svelte";
    import BrandCarousel from "./BrandCarousel.svelte";

    const { open } = getContext("simple-modal");

    export let pageIndex: number = 1;
    let operation = null;

    // PARAMETERS =========================================================
    const transitionTime = 200; // [ms] : transition time

    // VARIABLES =========================================================
    // global
    let error: string = ""; // if not empty, show the message instead of the content
    let loading;
    $: loading = $loader.loading; // the loading status in live
    let matchMedia = window.matchMedia("(max-width: 1600px)"); // only one page in small screen
    let zoomValue = 10; // value inn the zoom slider

    // viewer
    let pages: Page[] = []; // contain the page(s) in viewing
    let transitionMode: TransitionMode = "link"; // define the transition of the pages (move to right, to left or simply fade)
    let inTransition = { x: 0, duration: transitionTime }; // 'in' transition parameter
    let outTransition = { x: 0, duration: transitionTime }; // 'out' transition parameter

    // thumbnails
    let pageThumb: PageThumb[] = []; // contain all thumbnails of the catalog
    let bottomBarShow = false; // show/hide the bottom's bar of thumbnails
    let mouseInBottomBar = false; // if mouse is in the bottom's bar of thumbnails, used for vertically scroll

    // know if matchMedia query is matched
    let isMatchedMedia = matchMedia.matches;
    // list page for slideshow
    $: listPagesSlideShow = getListPagesSlideshow(pages, isMatchedMedia);

    $: scaleValue = zoomValueChanged(zoomValue);
    // verify if zoom is used
    $: isZoomedPageUsed = scaleValue == 1 ? false : true;

    // know if user scroll in zoom page
    let isDraggingScrollPageZoom = false;

    // Swipe Pages
    let isDraggingPageContainer = false;

    /**
     * container of pages
     */
    let pageContainer;

    // coordonate of mouse down dragging
    let draggingPageCooardonate: {
        x: number;
        y: number;
    };

    /**
     * user must do a swipe with minimum diff of 100px
     */
    const minimumDiffXSwipe = 60;

    /**
     * ensure the transition in term of the action by the user
     */
    $: if (transitionMode) {
        switch (transitionMode) {
            case "next":
                inTransition.x = 200;
                outTransition.x = -200;
                break;
            case "previous":
                inTransition.x = -200;
                outTransition.x = 200;
                break;
            case "link":
                inTransition.x = 0;
                outTransition.x = 0;
                break;
            default:
                inTransition.x = 0;
                outTransition.x = 0;
                break;
        }
    }

    // LIFE CYCLE =========================================================
    /**
     * download thumbnails pages and the first page
     */
    onMount(async () => {
        const loaderId = $loader.show();

        matchMedia.addEventListener("change", (c) => {
            isMatchedMedia = c.matches;
            // update page index (double page, simple page in function of media)
            checkPageIndex();
        });

        $api.getOperation().then((res) => {
            operation = res;
        });

        $api.getPages().then(
            (result) => {
                pages = result;
                checkPageIndex();
                if (pages.length > 0) {
                    $loader.hide(loaderId);
                } else {
                    error = "Aucune page disponible pour l'instant.";
                    $loader.hide(loaderId);
                }
            },
            (err) => {
                console.error(err);
                $loader.hide(loaderId);
            }
        );
    });

    /**
     * get list of pages for each thumbs list
     * @param listPages: Page list
     * @param isOnePage: boolean to know if one or double page is displayed
     */
    function getListPagesSlideshow(
        listPages: Page[],
        isOnePage: boolean
    ): PagesViewSlideshow[] {
        let listPagesSlideshow: PagesViewSlideshow[] = [];
        // if pages undefined
        if (!listPages || listPages.length === 0) return [];

        // add first page
        if (listPages.length > 0) {
            listPagesSlideshow.push({
                pages: [listPages[0]],
                index: 0,
            });
        }

        // add all other pages
        for (let i = 1; i < listPages.length - 1; i++) {
            let pagesItem: PagesViewSlideshow = {
                index: i,
                pages: [],
            };

            let page = listPages[i];

            pagesItem.pages.push(page);

            // if we would 2 pages, add second page in thumbnails
            if (!isOnePage && listPages.length - 1 >= i + 1) {
                pagesItem.pages.push(pages[i + 1]);
                i++;
            }
            listPagesSlideshow.push(pagesItem);
        }

        let lastIndex =
            !isOnePage && pages.length % 2 !== 0
                ? pages.length - 1
                : pages.length;

        // add last page
        // if it's double page and that have a odd number we don't add last page solo
        if (pages && pages.length > 1 && listPages.length === lastIndex) {
            listPagesSlideshow.push({
                pages: [listPages[pages.length - 1]],
                index: listPages.length - 1,
            });
        }
        return listPagesSlideshow;
    }

    function checkPageIndex() {
        if (pageIndex <= 0 || pageIndex > pages.length) {
            pageIndex = 1;
        } else if (
            !matchMedia.matches &&
            pageIndex !== 1 &&
            pageIndex % 2 === 1
        ) {
            // if index is odd, we display the previous page and the current page
            pageIndex = pageIndex - 1;
        }
    }

    /**
     * find uid in the pages list in terms of the index
     * @param id: number
     */
    function getUidPage(id: number): string {
        let index = id - 1;
        if (index < 0) {
            index = 0;
        } else if (index > pages.length - 1) {
            index = pages.length - 1;
        }
        return pages[index].uid;
    }

    function onNextPage() {
        let step = 2;
        if (matchMedia.matches || pageIndex === 1) {
            step = 1;
        }
        if (pageIndex < pages.length) {
            navigate(
                "/catalog/" +
                    $api.getOperationCode() +
                    "/" +
                    $api.getShopId() +
                    "/catalog/" +
                    (pageIndex + step > pages.length
                        ? pages.length
                        : pageIndex + step)
            );
        }
    }

    function onPreviousPage() {
        let step = 2;
        if (matchMedia.matches) {
            step = 1;
        }
        if (pageIndex > 1) {
            navigate(
                "/catalog/" +
                    $api.getOperationCode() +
                    "/" +
                    $api.getShopId() +
                    "/catalog/" +
                    (pageIndex - step < 1 ? 1 : pageIndex - step)
            );
        }
    }

    function goToPage(index: number) {
        bottomBarShow = false;
        if (!matchMedia.matches) {
            // index is odd, we need to go to the index-1 page
            if (index % 2 === 1 && index !== 1) {
                index = index - 1;
            }
        }
        navigate(
            "/catalog/" +
                $api.getOperationCode() +
                "/" +
                $api.getShopId() +
                "/catalog/" +
                index
        );
    }

    /**
     * show/hide the bottom's bar of thumbnails
     */
    async function onToggleBottomBar() {
        if (pageThumb.length === 0) {
            setTimeout(() => {
                bottomBarShow = !bottomBarShow;
            }, 100);
        } else {
            bottomBarShow = !bottomBarShow;
        }
    }

    /**
     * on arrows key down, get previous(left) / next(right) page, or, show(up) / hide(down) the bottom's bar of thumbnails
     * @param event
     */
    function handleKeydown(event) {
        if (!$loading) {
            switch (event.keyCode) {
                case 40: // 40: arrow down
                    if (bottomBarShow) {
                        onToggleBottomBar();
                    }
                    break;
                case 39: // 39: arrow right
                    if (pageIndex < pages.length) {
                        onNextPage();
                    }
                    break;
                case 38: // 38: arrow up
                    if (!bottomBarShow) {
                        onToggleBottomBar();
                    }
                    break;
                case 37: // 37: arrow left
                    if (pageIndex > 1) {
                        onPreviousPage();
                    }
                    break;
            }
        }
    }

    /**
     * on mouse enter the bottom's bar
     */
    function handleMouseenter() {
        mouseInBottomBar = true;
    }

    /**
     * on mouse leave the bottom's bar
     */
    function handleMouseleave() {
        mouseInBottomBar = false;
    }

    /**
     * scroll vertically the thumbnails when mouse is in the bottom's bar
     * @param event
     */
    function wheelEvent(event) {
        if (mouseInBottomBar) {
            const scrollContainer = document.querySelector(".listePageIn");
            let eventScroll = event.deltaY;
            if (scrollContainer) {
                scrollContainer.scrollLeft += eventScroll;
            }
        }
    }

    function zoomValueChanged(zoom: number): number {
        let scale = zoom / 10;

        // when zoom is max value and scroll doesn't used, scroll to center
        if (zoom > MIN_ZOOM_VALUE && pageContainer.scrollLeft == 0) {
            // zoom to center of container
            let x = (pageContainer.scrollWidth * scaleValue) / 2.5;
            changePageContainerScroll(x, 0);
        }
        return scale;
    }

    function zoomIn() {
        zoomValue === MAX_ZOOM_VALUE
            ? MAX_ZOOM_VALUE
            : (zoomValue += ZOOM_STEP);
    }

    function zoomOut() {
        zoomValue === MIN_ZOOM_VALUE
            ? MIN_ZOOM_VALUE
            : (zoomValue -= ZOOM_STEP);
    }

    /**
     * listen mouse down in page container to know if user swipe or scroll zoom
     * @param event: Event to get mouse down
     */
    function handleMouseDownPage(event) {
        // if zoom is unused change page
        if (!isZoomedPageUsed) {
            isDraggingPageContainer = true;
        } else {
            // zoom used user would scroll x axis page
            isDraggingScrollPageZoom = true;
        }

        // get event coordinate
        draggingPageCooardonate = {
            x: event.clientX,
            y: event.clientY,
        };
    }

    /**
     * when user termiate to swipe stop dragging
     */
    function handleMouseUpPage(event) {
        if (isDraggingPageContainer) {
            let endDraggingCooardonate = {
                x: event.clientX,
                y: event.clientY,
            };

            // if it's a swipe page
            if (isSwipePage(draggingPageCooardonate, endDraggingCooardonate)) {
                // change page
                let diff = draggingPageCooardonate.x - endDraggingCooardonate.x;
                if (diff > 0) {
                    // next page
                    onNextPage();
                } else {
                    // previous page
                    onPreviousPage();
                }
            }
        }
        // reset events values
        setTranslatePageContainerValue(0);
        draggingPageCooardonate = null;
        isDraggingPageContainer = false;
        isDraggingScrollPageZoom = false;
    }

    /**
     * set translate page container when user mouse move in compenent to change page
     * @param value: number translate value
     */
    function setTranslatePageContainerValue(value: number): void {
        pageContainer.style.transform = `translateX(${value * -1}px)`;
    }

    /**
     * listen move mouse for dragging
     * @param event
     */
    function handleMouseMovePage(event) {
        // if user would scroll x axis page
        if (isDraggingScrollPageZoom) {
            // compare coordinates
            let diff = draggingPageCooardonate.x - event.clientX;
            let diffHeight = draggingPageCooardonate.y - event.clientY;

            // calcul value to scroll
            let newScrollValueLeft = pageContainer.scrollLeft + diff;
            let newScrollValueTop = pageContainer.scrollTop + diffHeight;

            // update last dragggin mouse position
            draggingPageCooardonate.x = event.clientX;
            draggingPageCooardonate.y = event.clientY;
            // change value scroll
            changePageContainerScroll(newScrollValueLeft, newScrollValueTop);
        } else if (isDraggingPageContainer) {
            // if it's drag of page, set translation to page container
            let diff = draggingPageCooardonate.x - event.clientX;
            setTranslatePageContainerValue(diff);
        }
    }

    /**
     * change scroll container value
     * @param x: number, width scroll
     * @param y: number, height scroll
     */
    function changePageContainerScroll(x: number, y: number): void {
        if (x < 0) x = 0;
        if (x > pageContainer.scrollWidth) x = pageContainer.scrollWidth;

        if (y < 0) y = 0;
        if (y > pageContainer.scrollHeight) y = pageContainer.scrollHeight;

        tick().then(() => {
            pageContainer.scrollLeft = x;
            pageContainer.scrollTop = y;
        });
    }

    /**
     * verify if user swipe to change page
     * @param first: coordonate of first position
     * @param last: coordonate of last position
     */
    function isSwipePage(
        first: { x: number; y: number },
        last: { x: number; y: number }
    ): boolean {
        if (first && last) {
            let diffX = first.x - last.x;
            if (diffX < 0) diffX = last.x - first.x;
            if (diffX >= minimumDiffXSwipe) {
                return true;
            }
        }
        return false;
    }

    /**
     * zoom with wheel
     * @param e: Event
     */
    function wheelEventPageContainer(e): void {
        // detect if is trackpad (scroll in pages container) or mouse wheel (zoom/unzoom in pages container)
        let isTrackpad = false;
        if (e.wheelDeltaY != null && !isNaN(e.wheelDeltaY)) {
            if (Math.abs(e.wheelDeltaY) !== 120) {
                isTrackpad = true;
            }
        } else if (e.deltaMode === 0) {
            isTrackpad = true;
        }

        if (isTrackpad) {
            // event is scroll with trackpad
            // calcul value to scroll trackpad
            let newScrollValueLeft = pageContainer.scrollLeft + e.deltaX;
            let newScrollValueTop = pageContainer.scrollTop + e.deltaY;

            // change value scroll
            changePageContainerScroll(newScrollValueLeft, newScrollValueTop);
            e.preventDefault();
        }
    }

    /**
     * detect first position mobile swipe
     * @param e : Event to get touch position
     */
    function handleTouchStart(e) {
        if (e.touches && e.touches.length > 0 && !isZoomedPageUsed) {
            isDraggingPageContainer = true;

            // get event position
            draggingPageCooardonate = {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
            };
        }
    }

    /**
     * detect last position mobile swipe
     * @param e: Event touch end
     */
    function handleTouchEnd(e) {
        if (
            isDraggingPageContainer &&
            e.changedTouches &&
            e.changedTouches.length > 0
        ) {
            let endDraggingCooardonate = {
                x: e.changedTouches[0].clientX,
                y: e.changedTouches[0].clientY,
            };

            // it it's swipe
            if (isSwipePage(draggingPageCooardonate, endDraggingCooardonate)) {
                // change page
                let diff = draggingPageCooardonate.x - endDraggingCooardonate.x;
                if (diff > 0) {
                    // next page
                    onNextPage();
                } else {
                    // previous page
                    onPreviousPage();
                }
            }
        }
        // reset events values
        draggingPageCooardonate = null;
        isDraggingPageContainer = false;
    }

    /**
     * enable zoom
     * @param e: Event of db click
     */
    function enableZoom(e): void {
        if (zoomValue === MIN_ZOOM_VALUE) {
            zoomValue = MAX_ZOOM_VALUE;

            let rect = pageContainer.getBoundingClientRect();
            let x = e.clientX - rect.left;
            let y = rect.top;
            // change x,y value in function of scalevalue
            x = x * scaleValue;
            y = y * scaleValue;

            changePageContainerScroll(x, y);
        } else {
            // if max zoom set to min zoom
            zoomValue = MIN_ZOOM_VALUE;
        }
    }

    /**
     * zoom value to percent value
     * @param value
     */
    function zoomPercentValue(value: number): number {
        return (
            ((value - MIN_ZOOM_VALUE) / (MAX_ZOOM_VALUE - MIN_ZOOM_VALUE)) * 100
        );
    }
</script>

<!-- listen the window properties -->
<svelte:window on:keydown={handleKeydown} on:wheel={wheelEvent} />

<!-- show/hide the error message -->
{#if error === ""}
    <!-- catalog viewer -->
    {#if pages && pages.length > 0}
        <div
            id="pageViewer"
            class:stop-scrolling={mouseInBottomBar}
            class:has-errata={operation?.errata?.length > 0}
        >
            {#if $config.config.HideMenu}
                <div class="return mt-1 pl-2">
                    <Link
                        to="/catalog/{$api.getOperationCode() +
                            '/' +
                            $api.getShopId()}"
                    >
                        <span>
                            <i class="fas fa-chevron-left" /> Retour
                        </span>
                    </Link>
                </div>
            {/if}

            <!-- previous button -->
            <div class="navButton previous">
                <div>
                    {#if pageIndex > 1}
                        <button
                            on:click={onPreviousPage}
                            id="previousPage"
                            class="containerNavButton"
                            title="Afficher la page précédente"
                        >
                            <span class="icon">
                                <i class="fas fa-chevron-left" />
                            </span>
                        </button>
                    {/if}
                </div>
            </div>

            <!-- pages viewer -->
            <div
                id="pagesContainer"
                class="zoom-container"
                in:fly={inTransition}
                out:fly={outTransition}
                bind:this={pageContainer}
                class:zoommed={scaleValue > 1}
                style={isDraggingPageContainer || isDraggingScrollPageZoom
                    ? "cursor: grabbing;"
                    : "cursor: grab;"}
                on:mousedown={handleMouseDownPage}
                on:mouseup={handleMouseUpPage}
                on:mousemove={handleMouseMovePage}
                on:wheel={wheelEventPageContainer}
                on:touchstart={handleTouchStart}
                on:touchend={handleTouchEnd}
                on:dblclick={enableZoom}
                ondragstart="return false;"
                ondrop="return false;"
            >
                {#if pageIndex === 1 || pageIndex === pages.length || isMatchedMedia}
                    <div
                        class="cover-page zoomable"
                        style="transform:scale({scaleValue});"
                    >
                        <Catalog_PageView
                            uid={pages[pageIndex - 1].uid}
                            index={pageIndex}
                            side="right"
                        />
                    </div>
                {:else}
                    <div
                        class="zoomable"
                        style="transform:scale({scaleValue});"
                    >
                        {#each pages as page, i}
                            {#if pageIndex === i + 1 || pageIndex + 1 === i + 1}
                                {#if pageIndex === i + 1}
                                    <div id="page" class="first-page">
                                        <Catalog_PageView
                                            uid={page.uid}
                                            index={pageIndex}
                                            side="right"
                                        />
                                    </div>
                                {/if}
                                {#if pageIndex + 1 === i + 1}
                                    <div id="page" class="second-page">
                                        <Catalog_PageView
                                            uid={page.uid}
                                            index={pageIndex + 1}
                                            side="left"
                                        />
                                    </div>
                                {/if}
                            {/if}
                        {/each}
                    </div>
                {/if}
            </div>

            <!-- next button -->
            <div class="navButton next">
                <div>
                    {#if pages.length > pageIndex}
                        <button
                            on:click={onNextPage}
                            id="nextPage"
                            class="containerNavButton"
                            title="Afficher la page suivante"
                        >
                            <span class="icon">
                                <i class="fas fa-chevron-right" />
                            </span>
                        </button>
                    {/if}
                </div>
            </div>
        </div>
    {/if}
    <!--zoom slider-->
    <div class="zoomSlider">
        <button
            class="button-zoom minus"
            on:click={() => zoomOut()}
            disabled={zoomValue === MIN_ZOOM_VALUE}
        >
            <i class="fas fa-search-minus icon-zoom" />
        </button>
        <label class="zoomValue" for="zoom-value">
            {zoomPercentValue(zoomValue)}%
        </label>
        <button
            class="button-zoom plus"
            on:click={() => zoomIn()}
            disabled={zoomValue === MAX_ZOOM_VALUE}
        >
            <i class="fas fa-search-plus icon-zoom" />
        </button>
    </div>

    <!-- Thumbnails viewer (bottom's bar) -->

    <div class="listePage {$config.config.HideMenu ? 'webview' : ''}">
        <!-- show/hide button -->
        <div class="listePageTop">
            <div
                id="viewMiniature"
                on:click={onToggleBottomBar}
                title="Afficher/masquer toutes les pages. (flèche haut/bas)"
            >
                <span id="viewMiniatureButton" class="icon">
                    <i class="view_array" />
                </span>
            </div>
        </div>
        <!-- thumbnails list-->
        {#if pages !== null && pages.length > 0}
            <div
                class="listePageIn"
                class:active={bottomBarShow}
                on:mouseenter={handleMouseenter}
                on:mouseleave={handleMouseleave}
            >
                {#key pageIndex}
                    <Slideshow
                        {pageIndex}
                        listPagesCatalog={listPagesSlideShow}
                        on:go-to-page={(e) => goToPage(e.detail)}
                    />
                {/key}
            </div>
        {/if}
    </div>
{:else}
    <div id="error" class="p-5">
        <h1>{error}</h1>
    </div>
{/if}

<style lang="scss">
    @import "../styles/variables";

    .slider {
        -webkit-appearance: none;
        width: 100%;
        height: 15px;
        border-radius: 5px;
        background: #d3d3d3;
        outline: none;
        opacity: 0.7;
        -webkit-transition: 0.2s;
        transition: opacity 0.2s;
        &:hover {
            opacity: 1;
            height: 45px;
        }
        @media (max-width: $mobile-breakpoint) {
            display: none;
        }
    }

    .slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 25px;
        height: 25px;
        border-radius: 50%;
        background: $dark-mauve;
        cursor: pointer;
        &:hover,
        &:active {
            height: 40px;
            width: 40px;
        }
    }

    .slider::-moz-range-thumb {
        width: 25px;
        height: 25px;
        border-radius: 50%;
        background: $dark-mauve;
        cursor: pointer;
        &:hover {
            height: 40px;
            width: 40px;
        }
    }

    .zoomSlider {
        position: fixed;
        z-index: 11;
        display: flex;
        align-items: center;
        bottom: 1px;
        right: 30px;
        background-color: $dark-black;
        color: $light-white;

        border-radius: 25px;
        .zoomValue {
            color: $light-white;
            width: 3rem;
            text-align: center;

            @media (max-width: $mobile-breakpoint) {
                display: none;
            }
        }
        .button-zoom {
            background-color: $dark-black;
            color: $light-white;
            border: unset;
            width: 40px;
            height: 40px;
            cursor: pointer;
            transition: 0.3s ease;

            &:hover {
                background-color: $light-white;
                border-color: $dark-black;
                transition: 0.3s ease;
                color: $dark-black;
            }
            &.minus {
                border-top-left-radius: 25px;
                border-bottom-left-radius: 25px;
            }
            &.plus {
                border-top-right-radius: 25px;
                border-bottom-right-radius: 25px;
            }
        }
        @media (max-width: $mobile-breakpoint) {
            right: 0px;
            bottom: calc($menu-mobile-height + 5px);
        }

        // button {
        //     border-radius: 50%;
        //     height: 45px;
        //     width: 45px;
        //     color: $blue;
        // }
    }
    /* The slider itself */
    //   .slider {
    //     -webkit-appearance: none;  /* Override default CSS styles */
    //     appearance: none;
    //     background: #d3d3d3; /* Grey background */
    //     outline: none; /* Remove outline */
    //     opacity: 0.7; /* Set transparency (for mouse-over effects on hover) */
    //     -webkit-transition: .2s; /* 0.2 seconds transition on hover */
    //     transition: opacity .2s;
    //     width: 200px;
    //     @media (max-width: $mobile-breakpoint) {
    //         display: none;
    //     }
    //   }
    //   /* Mouse-over effects */
    //   .slider:hover {
    //     opacity: 1; /* Fully shown on mouse-over */
    //   }

    .zoom-container {
        overflow-x: auto;
        overflow-y: auto;
    }

    /**
  catalog viewer ========================================
   */
    #pageViewer {
        overflow-x: unset;
        position: relative;
        height: 100%;
        width: auto;
        display: inline-block;
        text-align: center;
        width: calc($main-content-width - 10px);

        @media (max-width: $mobile-breakpoint) {
            width: 100% !important;
            height: 100%;
        }

        .return {
            position: absolute;
            left: 0;
            color: $dark-black;
            font-weight: bold;
            z-index: 15;
        }
        .navButton {
            display: inline-block;
            position: absolute;
            top: 50%;
            z-index: 10;
            &.next {
                margin-left: auto;
                transform: translate(2%, -50%);
            }
            &.previous {
                margin-right: auto;
                transform: translate(-100%, -50%);
            }

            .containerNavButton {
                border-radius: 100%;
                background-color: $dark-black;
                color: $light-white;
                border: unset;
                width: 40px;
                height: 40px;
                box-shadow: 0 0 40px 0 rgba(0, 0, 0, 0.2);
                cursor: pointer;
                transition: 0.3s ease;

                &:hover {
                    background-color: $grey-darken;
                    transition: 0.3s ease;
                }
            }
            @media (max-width: $mobile-breakpoint) {
                #previousPage {
                    margin-right: -40px;
                }

                #nextPage {
                    margin-left: -40px;
                }
            }
        }
        .zoomable {
            transform-origin: top left;
        }
        #pagesContainer {
            //   display: flex;
            z-index: 9;
            height: 95%;
            width: auto;
            max-width: 95vw;
            display: inline-flex;
            margin: auto;
            @media (max-width: $mobile-breakpoint) {
                max-width: 90% !important;
            }

            @supports (-moz-appearance: none) {
                max-width: 60vw;
            }

            &.zoommed {
                background-image: url(/img/zoom-font.png);
                width: 90vw !important;
                max-width: unset !important;
                justify-content: center;
                overflow: auto;
            }
            #page {
                height: 100%;
                width: 50%;
                display: block;
                float: left;
            }
            .second-page {
                display: block;
            }
            .cover-page {
                height: 100%;
                width: auto;
                display: inline-block;
            }
        }
    }

    .stop-scrolling {
        height: 100%;
        overflow-y: hidden !important;
    }

    /**
  thumbnails viewer ========================================
   */

    .listePage {
        background-color: $dark-black;
        position: absolute;
        padding: 0;
        margin: 0;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 11;
        .listePageTop {
            position: absolute;
            padding: 0;
            display: flex;
            justify-content: center;
            width: 100%;

            #viewMiniature {
                width: 80px;
                height: 50px;
                text-align: center;
                position: absolute;
                left: calc(50% - 80px / 2);
                bottom: 0;
                border-top-left-radius: 6px;
                border-top-right-radius: 6px;
                background-color: $dark-black;
                cursor: pointer;
                overflow: hidden;

                #viewMiniatureButton {
                    display: inline-block;
                    width: 32px;
                    height: 32px;
                    border: none;
                    margin-top: calc((50px + 8px - 32px) / 2);
                }
            }
        }

        .listePageIn {
            display: flex;
            overflow: auto;
            width: 75%;
            margin: auto;
            height: 0;
            padding: 0 15px;
            transition-duration: 0.3s;

            &.active {
                width: 95%;
                height: 82vh !important;
                padding: 15px !important;
                transition-duration: 0.3s;
                @media (max-width: $mobile-breakpoint) {
                    height: 65vh !important;
                }
            }
        }
    }

    .thumbnail {
        display: flex;
        flex-wrap: nowrap;
        margin: 5px 10px;
        align-content: center;
        transition-duration: 0.1s;

        &:hover {
            outline: 5px $dark-mauve solid;
            cursor: pointer;
            transform: scale(1.1);
            transition-duration: 0.1s;
        }

        &.active {
            border: 3px $dark-mauve solid;
            outline: 3px white solid;
            filter: opacity(0.5);
        }

        img {
            min-width: 100px;
            height: 100%;
        }
    }

    @media (max-width: 1600px) {
        .zoomable.first-page {
            height: 100%;
        }
        .first-page {
            width: 100% !important;
        }
        .second-page {
            display: none !important;
        }
        .cover-page {
            width: 100% !important;
        }
    }

    @media (max-width: 1024px) {
        .listePage {
            .listePageIn {
                width: 100%;
            }
        }
        .thumbnail {
            img {
                min-width: 130px;
            }
        }
    }

    /**
    error viewer ========================================
  */

    #error {
        background-color: white;
        width: 70%;
        position: absolute !important;
        transform: translateX(-50%) translateY(-50%);
        top: 50%;
        left: 50%;
        text-align: center;
        z-index: 9999;

        h1 {
            margin: auto;
            font-weight: bold;
            color: $dark-mauve;
            font-size: 2em;
        }
    }

    @media (max-width: $mobile-breakpoint) {
        #pageViewer {
            &.has-errata {
                height: calc(100% - $errata-menu-mobile-height);
            }
            .navButton {
                // background-color: #d7d7d7;
            }
        }

        #leftMenu {
            display: none;
        }

        .listePage {
            bottom: $menu-mobile-height;

            &.webview {
                bottom: 0;
            }
        }
    }

    @media (max-width: 600px) {
        .navButton {
            .containerNavButton {
                width: 45px !important;
                height: 45px !important;
            }
        }
    }
</style>
